/**
 * navigation/RootNavigator
 * ──────────────────────────────────────────────────────────────────────────
 * The three states the app can be in, in the order they resolve:
 *
 *   1. Probing the session          → nothing but an indicator
 *   2. No session                   → hand off to skplatform-fe
 *   3. Signed in, workspace loading → indicator
 *   4. Signed in, console gate unmet, Finance → Onboarding, which REPLACES the
 *      console. Not a modal over an empty dashboard: without a base currency
 *      and a threshold, no figure the console could show would mean anything.
 *   5. Otherwise                    → the shell
 *
 * An approver or a custodian in an unconfigured workspace does NOT get the
 * onboarding screen — they cannot set a base currency, and showing them a setup
 * form they have no permission to complete would be a dead end. They get the
 * shell, whose screens each explain what is missing.
 * ──────────────────────────────────────────────────────────────────────────
 */
import { useEffect } from 'react';
import { StyleSheet, Text, View } from 'react-native';
import { Button, Loading } from '../components';
import { neutral } from '../theme/colors';
import { sans, size as fontSize, leading, type as textRole } from '../theme/typography';
import { isWeb } from '../lib/responsive';
import { redirectToSignIn } from '../services/session/session.service';
import { useConfig } from '../context/ConfigContext';
import { useSession } from '../context/SessionContext';
import OnboardingScreen from '../screens/Onboarding';
import AppShell from './AppShell';

export function RootNavigator() {
  const { status } = useSession();
  const { ready, console: role, gates } = useConfig();

  // On the web the hand-off is automatic: there is a sign-in app to go to, and
  // making the user press a button to reach it is friction with no purpose.
  useEffect(() => {
    if (status === 'signed-out' && isWeb) redirectToSignIn();
  }, [status]);

  if (status === 'loading') return <Loading full />;
  if (status === 'signed-out') return <SignInRequired />;
  if (!ready) return <Loading full label="Opening your workspace" />;

  // Setup replaces the console — and only for the console that can complete it.
  if (!gates.console && role === 'finance') return <OnboardingScreen />;

  return <AppShell />;
}

/**
 * Shown while the web redirect is in flight, and as the resting state on
 * Android/iOS — where the platform app owns the token hand-off and there is
 * nowhere for this app to redirect to.
 */
function SignInRequired() {
  return (
    <View style={styles.wrap}>
      <Text style={textRole.pageTitle}>Sign in to continue</Text>
      <Text style={styles.blurb}>
        SK Company Cash uses your Softknack account. Sign in through the platform and you will be
        returned here.
      </Text>
      {isWeb && (
        <Button label="Go to sign in" size="xl" variant="primary" onPress={redirectToSignIn} />
      )}
    </View>
  );
}

const styles = StyleSheet.create({
  wrap: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    gap: 12,
    padding: 32,
    backgroundColor: neutral.surface,
  },
  blurb: {
    ...sans('400'),
    fontSize: fontSize.button,
    lineHeight: leading(fontSize.button),
    color: neutral.grey600,
    textAlign: 'center',
    maxWidth: 420,
  },
});

export default RootNavigator;
